/**
 * Local form state, derived flags, and provider-selection ref
 * for the Comment RPA "mark as completed" modal.
 *
 * @module commentRPAnewModal/hooks/useCommentRpaFormState
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import { AuthOnFile } from "../../../../data-model/medicalPaOrdersModel";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";
import { UserGeneralInfoContextKeys } from "../../../../enums/userGeneralInfoContextKeys";
import { RootState } from "../../../../redux/store/store";
import { UserStore } from "../../../../store/UserGeneralInfoStore";
import { determineIfDocumentRpaShouldRun } from "../../../../utils/determineIfDocumentRpaShouldRun";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

/** Parameters for {@link useCommentRpaFormState}. */
interface UseCommentRpaFormStateParams {
  /** EV write-back status string. */
  wrtiteBackStatus: string;
  /** Document RPA status string. */
  docRpaStatus: string;
  /** Optional BO status prop for pre-selecting the badge. */
  boStatus?: string | { id: string; text: string };
  /**
   * Order-level BO default (`status.bo_value` or `status.bo_status`) when
   * `boStatus` is not passed explicitly.
   */
  defaultBoFromOrder?: string | null;
  /**
   * Order-level estimate default (`status.estimate_value`). Used in place of
   * `defaultBoFromOrder` to seed the badge for orgs in `exceptionFlowForUrologyOrgs`
   * that repurpose the BO column as an estimate value.
   */
  defaultEstimateValueFromOrder?: string | null;
  /** Auth-on-file payload; drives the document-RPA gating decision. */
  authOnFile?: AuthOnFile | null;
}

/**
 * Manages local form state for the Comment RPA modal: badge dropdown (from
 * `boOptions` or `estimateOptions` depending on org), provider-reassignment ref,
 * and derived flags for the warning banner and write-back report inclusion.
 *
 * @param params - Write-back, doc-RPA, BO/estimate defaults, and optional badge seed
 * @returns Form state, setters, derived flags, and the selected-provider ref
 */
export const useCommentRpaFormState = ({
  wrtiteBackStatus,
  docRpaStatus,
  boStatus,
  defaultBoFromOrder,
  defaultEstimateValueFromOrder,
  authOnFile,
}: UseCommentRpaFormStateParams) => {
  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const { data: estimateOptions } = useSelector(
    (state: RootState) => state.estimateOptions,
  );
  const defaultBadge = UserStore.getItem(
    UserGeneralInfoContextKeys.GLOBAL_BO_VALUE,
  );
  const isSunstateUrology = isExceptionFlowForUrologyOrg(
    getOrgIdForFetchExternalWorklist(),
  );

  const [boStatusOptions, setBoStatusOptions] = useState<BadgeWithIconProps[]>(
    [],
  );
  const [selectedBadge, setSelectedBadge] = useState<BadgeWithIconProps | null>(
    defaultBadge || {
      id: "",
      text: "Select Status",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    },
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const selectedProvider = useRef<{ id: string; name: string }>({
    id: "",
    name: "",
  });

  /** @returns `true` when a non-empty provider has been selected in the ref. */
  const hasProviderSelection = () => {
    const { id, name } = selectedProvider.current;
    return id.trim() !== "" || name.trim() !== "";
  };

  const shouldIncludeWriteBackReports = useMemo(() => {
    return (
      wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
      wrtiteBackStatus === EVWriteBackStatus.ERROR
    );
  }, [wrtiteBackStatus]);

  const determineTextForWarningBanner = useMemo(() => {
    const evNotDone =
      wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
      wrtiteBackStatus === EVWriteBackStatus.ERROR;
    const docNotDone =
      (docRpaStatus === DocWriteBackStatus.NOT_INITIATED ||
        docRpaStatus === DocWriteBackStatus.ERROR) &&
      determineIfDocumentRpaShouldRun(authOnFile);

    if (evNotDone && docNotDone) return " Comment RPA & Document RPA";
    if (evNotDone) return "Comment RPA";
    if (docNotDone) return "Document RPA";
    return "";
  }, [wrtiteBackStatus, docRpaStatus, authOnFile]);

  useEffect(() => {
    const optionsSource = isSunstateUrology ? estimateOptions : boOptions;

    if (optionsSource) {
      const mapped = optionsSource.map((option) => ({
        id: option.value,
        text: option.label,
        bgColor: "#F5F5F5",
        textColor: "#0F0F0F",
      }));
      setBoStatusOptions(mapped);

      // Exception-flow orgs seed the badge from the order's estimate value; all
      // other orgs seed from the BO default.
      const orderSeedDefault = isSunstateUrology
        ? defaultEstimateValueFromOrder
        : defaultBoFromOrder;

      let seed: string | undefined;
      if (boStatus) {
        seed =
          typeof boStatus === "object"
            ? boStatus.id || boStatus.text
            : boStatus;
      } else if (orderSeedDefault) {
        seed = orderSeedDefault;
      } else if (defaultBadge) {
        seed = defaultBadge;
      }
      if (seed) {
        const match = mapped.find((o) => o.id === seed || o.text === seed);
        if (match) setSelectedBadge(match);
      }
    }
  }, [
    boOptions,
    estimateOptions,
    boStatus,
    defaultBoFromOrder,
    defaultEstimateValueFromOrder,
    isSunstateUrology,
  ]);

  return {
    selectedBadge,
    setSelectedBadge,
    boStatusOptions,
    selectedProvider,
    hasProviderSelection,
    shouldIncludeWriteBackReports,
    determineTextForWarningBanner,
    isLoading,
    setIsLoading,
  };
};

/** Return type of the {@link useCommentRpaFormState} hook. */
export type CommentRpaFormState = ReturnType<typeof useCommentRpaFormState>;
